import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { addCourseReview, getCourseReviews, deleteReview, editCourseReview } from '../../../../services/userService';
import { toast } from 'react-toastify';
import { capitalizeFirstOnChange } from '../../../../utils/textFormat';

const DefaultImage = '/images/profile/default-profile.png';

function CourseRating({ contentData }) {
  const [ratings, setRatings] = useState([]);
  const [showInput, setShowInput] = useState(true);
  const [ratingText, setRatingText] = useState('');
  const [selectedRating, setSelectedRating] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUserRated, setHasUserRated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editReviewId, setEditReviewId] = useState(null);

  const videoId = contentData?.id;
  const courseId = contentData?.course_id;

  const user = JSON.parse(localStorage.getItem('user'));
  const userId = user?.id;

  useEffect(() => {
    if (videoId && courseId) {
      fetchReviews();
    }
  }, [videoId, courseId]);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      const response = await getCourseReviews(courseId, videoId);
      if (response.success) {
        setRatings(response.data);
        const userHasRated = response.data.some(rating => rating.user_id === userId);
        setHasUserRated(userHasRated);
        setShowInput(!userHasRated);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStarClick = (rating) => {
    setSelectedRating(rating);
  };

  const handleSaveRating = async () => {
    if (selectedRating > 0 && (!hasUserRated || isEditing)) {
      try {
        let response;
        if (isEditing && editReviewId) {
          response = await editCourseReview({
            id: editReviewId,
            class_rating: selectedRating,
            review_text: ratingText
          });
          if (response.success) {
            toast.success('Review updated successfully');
          }
        } else {
          response = await addCourseReview({
            course_id: courseId,
            video_id: videoId,
            class_rating: selectedRating,
            mentor_rating: 0,
            review_text: ratingText
          });
          if (response.success) {
            toast.success('Review added successfully');
          }
        }

        if (response.success) {
          await fetchReviews();
          setRatingText('');
          setSelectedRating(0);
          setShowInput(false);
          setIsEditing(false);
          setEditReviewId(null);
        }
      } catch (error) {
        console.error('Error saving review:', error);
        toast.error('Failed to save review');
      }
    }
  };

  const handleDeleteReview = async (review_id) => {
    try {
      const response = await deleteReview(review_id);
      if (response.success) {
        await fetchReviews();
        toast.success('Review deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const handleEditReview = (rating) => {
    setIsEditing(true);
    setEditReviewId(rating.review_id);
    setRatingText(rating.review_text || '');
    setSelectedRating(rating.class_rating || 0);
    setShowInput(true);
  };

  const renderStars = (rating, interactive = false) => {
    return Array.from({ length: 5 }, (_, index) => {
      const filled = index < rating;
      return (
        <button
          key={index}
          type="button"
          className={`p-1 rounded transition ${interactive ? 'hover:scale-110' : ''} ${filled ? 'text-amber-500' : 'text-gray-300'}`}
          onClick={() => interactive && handleStarClick(index + 1)}
          aria-label={interactive ? `Rate ${index + 1} star${index + 1 > 1 ? 's' : ''}` : undefined}
        >
          <Icon icon={filled ? 'mdi:star' : 'mdi:star-outline'} width="24" height="24" />
        </button>
      );
    });
  };

  if (!videoId || !courseId) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-500">No ratings available for this content.</p>
      </div>
    );
  }

  return (
    <div>
      {hasUserRated && !isEditing && (
        <div className="mb-6 p-4 rounded-xl border border-gray-200 bg-gray-50/50 text-sm text-gray-600">
          You’ve already submitted a review. You can edit it from the list below.
        </div>
      )}
      {showInput && (
        <div className="mb-6 p-4 rounded-xl border border-gray-200 bg-gray-50/50">
          <p className="text-sm font-medium text-font-primary mb-2">Your rating</p>
          <div className="flex gap-1 mb-4">{renderStars(selectedRating, !hasUserRated || isEditing)}</div>
          <textarea
            className="w-full min-h-[100px] px-4 py-3 rounded-lg border border-gray-200 bg-white text-font-primary placeholder:text-font-secondary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
            placeholder="Write your review..."
            value={ratingText}
            onChange={(e) => setRatingText(capitalizeFirstOnChange(e.target.value))}
            autoCapitalize="sentences"
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              className="px-4 py-2 rounded-lg font-medium text-font-secondary hover:bg-gray-200 transition"
              onClick={() => {
                setRatingText('');
                setSelectedRating(0);
                setShowInput(false);
                setIsEditing(false);
                setEditReviewId(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
              onClick={handleSaveRating}
              disabled={selectedRating === 0 || (hasUserRated && !isEditing)}
            >
              {isEditing ? 'Update' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {ratings.length > 0 ? (
          ratings.map((rating) => (
            <div key={rating.review_id} className="p-4 rounded-xl border border-gray-200 bg-white">
              <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <img
                    src={rating.user?.profile_picture || DefaultImage}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover border border-gray-200 shrink-0"
                  />
                  <span className="font-medium text-font-primary truncate">{rating.user?.name || 'Anonymous'}</span>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-font-secondary">{new Date(rating.review_createdAt).toLocaleDateString()}</span>
                  {userId === rating.user_id && (
                    <>
                      <button
                        type="button"
                        className="p-2 rounded-lg text-font-secondary hover:bg-gray-100 hover:text-primary transition"
                        onClick={() => handleEditReview(rating)}
                        title="Edit review"
                      >
                        <Icon icon="mdi:pencil" width="16" height="16" />
                      </button>
                      <button
                        type="button"
                        className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition"
                        onClick={() => handleDeleteReview(rating.review_id)}
                        title="Delete review"
                      >
                        <Icon icon="mdi:delete" width="16" height="16" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-0.5 mb-2">{renderStars(rating.class_rating)}</div>
              {rating.review_text && <p className="text-sm text-font-secondary leading-relaxed">{rating.review_text}</p>}
            </div>
          ))
        ) : (
          <div className="py-8 text-center text-sm text-font-secondary">
            No ratings yet. Be the first to rate this content!
          </div>
        )}
      </div>
    </div>
  );
}

export default CourseRating;